import { X } from '@phosphor-icons/react';
import clsx from 'clsx';
import {
    type ReactNode,
    useEffect,
    useRef,
    useState,
} from 'react';
import { createPortal } from 'react-dom';

type DetailDrawerProps = {
    ariaLabel: string;
    children: ReactNode;
    closeLabel: string;
    header: ReactNode;
    onClose: () => void;
    onCloseStart?: () => void;
    restoreFocus?: boolean;
    size?: 'default' | 'compact' | 'inspector';
};

const DetailDrawer = ({
    ariaLabel,
    children,
    closeLabel,
    header,
    onClose,
    onCloseStart,
    restoreFocus = false,
    size = 'default',
}: DetailDrawerProps) => {
    const [isMounted, setIsMounted] = useState(false);
    const [isAnimatingOut, setIsAnimatingOut] = useState(false);
    const closeTimerRef = useRef<number | null>(null);
    const previousFocusRef = useRef<HTMLElement | null>(null);

    useEffect(() => {
        if (!restoreFocus) return undefined;
        previousFocusRef.current = document.activeElement instanceof HTMLElement
            ? document.activeElement
            : null;
        return () => previousFocusRef.current?.focus();
    }, [restoreFocus]);

    useEffect(() => {
        const timer = window.setTimeout(() => setIsMounted(true), 16);
        return () => window.clearTimeout(timer);
    }, []);

    const handleClose = () => {
        if (isAnimatingOut) return;
        onCloseStart?.();
        setIsMounted(false);
        setIsAnimatingOut(true);
        closeTimerRef.current = window.setTimeout(onClose, 300);
    };

    useEffect(() => () => {
        if (closeTimerRef.current !== null) {
            window.clearTimeout(closeTimerRef.current);
        }
    }, []);

    return createPortal(
        <>
            <button
                aria-label={closeLabel}
                className={clsx(
                    'fixed inset-0 z-[500] cursor-default transition-[opacity,background-color,backdrop-filter] ease-[cubic-bezier(0.16,1,0.3,1)]',
                    isMounted
                        ? 'bg-[#1A162F]/20 opacity-100 backdrop-blur-[1px] duration-200'
                        : 'bg-[#F4F5F8]/0 opacity-0 backdrop-blur-0 duration-150',
                )}
                onClick={handleClose}
                type="button"
            />
            <aside
                aria-label={ariaLabel}
                aria-modal="true"
                className={clsx(
                    'fixed bottom-0 right-0 top-0 z-[510] flex w-full flex-col overflow-hidden border-l border-[#E8EBF3] bg-white transition-[transform,box-shadow] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] sm:rounded-l-[24px]',
                    size === 'inspector'
                        ? 'sm:w-[640px] sm:max-w-[calc(100vw-72px)]'
                        : size === 'compact'
                            ? 'sm:w-[720px] sm:max-w-[96vw]'
                            : 'sm:w-[1040px] sm:max-w-[98vw]',
                    isMounted
                        ? 'translate-x-0 shadow-[-24px_0_60px_rgba(26,22,47,0.12)]'
                        : 'translate-x-full shadow-[0_0_0_rgba(26,22,47,0)]',
                )}
                role="dialog"
            >
                <header className="px-6 py-5">
                    <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0 flex-1">
                            {header}
                        </div>
                        <button
                            aria-label={closeLabel}
                            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl text-[#8A93B2] transition-colors hover:bg-[#F4F5F8] hover:text-[#1A162F]"
                            onClick={handleClose}
                            type="button"
                        >
                            <X size={16} weight="bold" />
                        </button>
                    </div>
                </header>
                {children}
            </aside>
        </>,
        document.body,
    );
};

export default DetailDrawer;
